<template>
  <div class="main">
    <Spinner class="is-size-3" v-if="isLoading" />

    <div class="notification is-danger" v-if="huntAreaLoadError">{{ huntAreaLoadError }}</div>

    <div class="container" v-if="huntArea">
      <!-- Top card -->
      <div class="card">
        <!-- Top card header -->
        <header class="card-header">
          <div class="card-header-title">
            <div class="media">
              <div class="media-content">
                <p class="title is-4">{{ huntArea.name }}</p>
              </div>
            </div>
          </div>
        </header>

        <!-- Top card content -->
        <div class="card-content hunt-area-info">
          <div class="content">
            <div class="table-container">
              <table class="table is-narrow">
                <tbody>
                  <tr>
                    <th class="is-narrow">
                      <span class="is-pulled-right">Id</span>
                    </th>
                    <td>
                      <span>{{ huntArea.id }}_LLGroup</span>
                    </td>

                    <th class="is-narrow">
                      <span class="is-pulled-right">Has Border</span>
                    </th>
                    <td>
                      <Spinner width="20" height="20" v-if="isLoadingHuntAreaInfo" />
                      <span class="has-text-danger" v-if="huntAreaInfoLoadFailed">[failed]</span>
                      <span :class="hasBorderTextClass" v-if="huntAreaInfo">
                        {{ huntAreaInfo.hasBorder ? "YES" : "NO" }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th class="is-narrow">
                      <span class="is-pulled-right">Created At</span>
                    </th>
                    <td>
                      <span>{{ huntArea.createdAt }}</span>
                    </td>

                    <th class="is-narrow">
                      <span class="is-pulled-right">PoIs Count</span>
                    </th>
                    <td>
                      <Spinner width="20" height="20" v-if="isLoadingHuntAreaInfo" />
                      <span class="has-text-danger" v-if="huntAreaInfoLoadFailed">[failed]</span>
                      <span v-if="huntAreaInfo">
                        {{ huntAreaInfo.markersCount }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th class="is-narrow">
                      <span class="is-pulled-right">Admin</span>
                    </th>
                    <td>
                      <span v-if="huntArea.createdBy">
                        <a @click="changeAdminClicked">
                          <span>
                            <span v-if="nameOfUserWithId(huntArea.createdBy)">{{ nameOfUserWithId(huntArea.createdBy) }} (<span>{{ huntArea.createdBy }}_LLUser</span>)</span>
                            <span v-else>{{ huntArea.createdBy }}_LLUser</span>
                            <i class="icon-edit-2"></i>
                          </span>
                        </a>
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th class="is-narrow">
                      <span class="is-pulled-right">County</span>
                    </th>
                    <td>
                      <span>{{ huntArea.county }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom tabs -->
      <div class="tabs is-toggle is-fullwidth bottom-tabs">
        <ul>
          <li v-for="tab in bottomTabs"
            :key="tab.index"
            :class="{'is-active': tab.index === selectedBottomTabIndex}"
            @click="selectBottomTab(tab)">

            <a>{{tab.name}}</a>
          </li>
        </ul>
      </div>

      <!-- Bottom tab content -->
      <div class="tabs-content">
        <component
          :is="selectedBottomTabComponent"
          :huntAreaStorePath="huntAreaStorePath"
          :usersCacheStorePath="usersCacheStorePath" />
      </div>

      <!-- Modals -->
      <HuntAreaChangeAdminModal
        v-if="showChangeAdminModal"
        :huntAreaStorePath="huntAreaStorePath"
        :huntArea="huntArea"
        @cancelled="closeChangeAdminModal"
        @succeeded="closeChangeAdminModal"
      />
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/utils/Spinner'
import HuntAreaMembersTab from './HuntAreaMembersTab'
import HuntAreaGamecamConfigsTab from './HuntAreaGamecamConfigsTab'
import HuntAreaChangeAdminModal from './HuntAreaChangeAdminModal'
import HuntAreaActivityLogTab from './HuntAreaActivityLogTab'

import {
  LOAD_USERS_WITH_IDS,
  GET_USER_WITH_ID
} from '@/modules/users/usersCacheStore.js'

import {
  IS_LOADING_HUNT_AREA,
  GET_HUNT_AREA,
  GET_HUNT_AREA_LOAD_ERROR
} from './huntAreaStore.js'

import {
  LOAD_HUNT_AREA_INFO_FOR_HUNT_AREA_IDS,
  IS_LOADING_HUNT_AREA_INFO_FOR_HUNT_AREA_ID,
  GET_HUNT_AREA_INFO_FOR_HUNT_AREA_ID,
  GET_HUNT_AREA_INFO_LOAD_ERROR_FOR_HUNT_AREA_ID
} from './huntAreasCacheStore'

export default {
  name: 'HuntArea',

  components: {
    Spinner,
    HuntAreaChangeAdminModal,
    HuntAreaActivityLogTab
  },

  props: {
    huntAreaStorePath: {
      type: String,
      required: true
    },

    usersCacheStorePath: {
      type: String,
      required: true
    },

    huntAreasCacheStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      bottomTabs: [
        {
          index: 0,
          name: "Members",
          component: HuntAreaMembersTab
        },
        {
          index: 1,
          name: "Gamecam Configs",
          component: HuntAreaGamecamConfigsTab
        },
        {
          index: 2,
          name: "Activity Log",
          component: HuntAreaActivityLogTab
        }
      ],
      selectedBottomTabIndex: null,
      showChangeAdminModal: false
    };
  },

  mounted() {
    this.loadCreatorUser()
    this.loadHuntAreaInfo()
  },

  methods: {
    loadCreatorUser() {
      if (!this.huntArea) {
        return
      }

      const loadUserWithIdsAction = `${this.usersCacheStorePath}/${LOAD_USERS_WITH_IDS}`
      const userIds = [this.huntArea.createdBy]
      this.$store.dispatch(loadUserWithIdsAction, { userIds })
    },

    loadHuntAreaInfo() {
      if (!this.huntArea) {
        return
      }

      const loadHuntAreasInfoAction = `${this.huntAreasCacheStorePath}/${LOAD_HUNT_AREA_INFO_FOR_HUNT_AREA_IDS}`
      const huntAreaIds = [this.huntArea.id]
      this.$store.dispatch(loadHuntAreasInfoAction, { huntAreaIds })
    },

    selectBottomTab(tab) {
      this.selectedBottomTabIndex = tab.index
    },

    nameOfUserWithId(userId) {
      if (!userId) {
        return null
      }

      const getUserWithIdPath = `${this.usersCacheStorePath}/${GET_USER_WITH_ID}`
      const user = this.$store.getters[getUserWithIdPath](userId)

      if (!user) {
        return null
      }

      return `${user.firstName} ${user.lastName}`
    },

    changeAdminClicked() {
      this.showChangeAdminModal = true
    },

    closeChangeAdminModal() {
      this.showChangeAdminModal = false
    }
  },

  computed: {
    isLoading() {
      const isLoadingHuntAreaPath = `${this.huntAreaStorePath}/${IS_LOADING_HUNT_AREA}`
      return this.$store.getters[isLoadingHuntAreaPath]
    },

    huntArea() {
      const getHuntAreaPath = `${this.huntAreaStorePath}/${GET_HUNT_AREA}`
      return this.$store.getters[getHuntAreaPath]
    },

    huntAreaLoadError() {
      const getHuntAreaLoadErrorPath = `${this.huntAreaStorePath}/${GET_HUNT_AREA_LOAD_ERROR}`
      return this.$store.getters[getHuntAreaLoadErrorPath]
    },

    selectedBottomTabComponent() {
      if (this.selectedBottomTabIndex === null) {
        return null
      }

      return this.bottomTabs[this.selectedBottomTabIndex].component
    },

    isLoadingHuntAreaInfo() {
      if (!this.huntArea) {
        return false;
      }

      const isLoadingHuntAreaInfo = `${this.huntAreasCacheStorePath}/${IS_LOADING_HUNT_AREA_INFO_FOR_HUNT_AREA_ID}`;
      return this.$store.getters[isLoadingHuntAreaInfo](this.huntArea.id);
    },

    huntAreaInfo() {
      if (!this.huntArea) {
        return null;
      }

      const getHuntAreaInfo = `${this.huntAreasCacheStorePath}/${GET_HUNT_AREA_INFO_FOR_HUNT_AREA_ID}`;
      return this.$store.getters[getHuntAreaInfo](this.huntArea.id);
    },

    huntAreaInfoLoadFailed() {
      if (!this.huntArea) {
        return null;
      }

      const getHuntAreaInfoLoadError = `${this.huntAreasCacheStorePath}/${GET_HUNT_AREA_INFO_LOAD_ERROR_FOR_HUNT_AREA_ID}`;
      if (this.$store.getters[getHuntAreaInfoLoadError](this.huntArea.id)) {
        return true
      }

      return false
    },

    hasBorderTextClass() {
      if (!this.huntAreaInfo) {
        return {}
      }

      return {
        "has-text-danger": !this.huntAreaInfo.hasBorder,
        "has-text-success": this.huntAreaInfo.hasBorder
      };
    }
  },

  watch: {
    huntArea() {
      // Make sure all modals are closed when the hunt area changes.
      // This may happen, for example, when the hunt area is reloaded after the admin was changed.
      this.showChangeAdminModal = false

      this.loadCreatorUser()
      this.loadHuntAreaInfo()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/style.scss";

.main {
  overflow: auto;
  padding: 10px;
}

.hunt-area-info .table {
  th {
    padding-right: 0 !important;
    border-bottom: 0;

    span {
      font-weight: normal;
    }
  }

  td {
    border-bottom: 0;

    span {
      font-weight: bold;
      white-space: nowrap;
    }
  }

  .loader {
    margin-top: 5px;
  }
}

.bottom-tabs.tabs {
  margin-top: $column-gap * 2;

  a {
    padding: 4px 16px;
  }
}

.tabs-content {
  overflow: auto;
}
</style>
